import React, { useContext, useMemo, useState } from "react";

import { AuthContext } from "../context/AuthContext";
import DocumentUpload from "../components/DocumentUpload";
import ProfileModal from "../components/ProfileModal";
import ChangePasswordModal from "../components/ChangePasswordModal";
import LoadingSpinner from "../components/LoadingSpinner";

const Settings = () => {
  const { user, refreshUser, loading } = useContext(AuthContext);
  const [activeModal, setActiveModal] = useState(null);

  const profileDetails = useMemo(() => {
    if (!user) return [];
    return [
      { label: "Username", value: user.username || "Not provided" },
      { label: "Role", value: user.role || "Not provided" },
      {
        label: "Full name",
        value: [user.firstName, user.middleName, user.lastName].filter(Boolean).join(" ") || "Not provided",
      },
      { label: "Phone number", value: user.phoneNumber || "Not provided", isMissing: !user?.phoneNumber },
      {
        label: "Date of birth",
        value: user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString() : "Not provided",
        isMissing: !user?.dateOfBirth,
      },
      {
        label: "Bank name",
        value: user.bankName || "Not provided",
        isMissing: !user?.bankName,
      },
      {
        label: "Account name",
        value: user.bankAccountName || "Not provided",
        isMissing: !user?.bankAccountName,
      },
      {
        label: "Account number",
        value: user.bankAccountNumber || "Not provided",
        isMissing: !user?.bankAccountNumber,
      },
      {
        label: "BVN",
        value: user.bvn || "Not provided",
        isMissing: !user?.bvn,
      },
    ];
  }, [user]);

  const pendingFields = profileDetails.filter((detail) => detail.isMissing).length;

  const actionCards = [
    {
      key: "profile",
      title: "Profile details",
      description: "Review personal and banking information, and add any missing details.",
    },
    {
      key: "password",
      title: "Change password",
      description: "Update the password you use to access the Ecoops Wealth portal.",
    },
  ];

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-xl">
          <h2 className="text-xl font-semibold text-slate-900">Session expired</h2>
          <p className="mt-2 text-sm text-slate-500">Please sign in again to manage your account settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 py-10">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-10 px-4 md:px-8">
        <section className="rounded-3xl border border-white/40 bg-white/95 p-8 shadow-lg shadow-slate-900/5 backdrop-blur">
          <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Account settings</h1>
              <p className="mt-1 text-sm text-slate-500">
                Keep your personal and banking information accurate to ensure smooth transactions and compliance.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
              <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              {pendingFields === 0 ? "Profile complete" : `${pendingFields} detail${pendingFields > 1 ? "s" : ""} pending`}
            </div>
          </div>

          <dl className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {profileDetails.map(({ label, value, isMissing }) => (
              <div
                key={label}
                className="rounded-xl border border-slate-100 bg-slate-50/60 p-4 shadow-inner shadow-white/40"
              >
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                <dd
                  className={`mt-2 text-sm font-medium ${isMissing ? "text-amber-600" : "text-slate-900"}`}
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="grid gap-6 md:grid-cols-2">
          {actionCards.map((card) => (
            <button
              key={card.key}
              type="button"
              onClick={() => setActiveModal(card.key)}
              className="group relative flex h-full flex-col justify-between rounded-2xl border border-slate-100 bg-white/95 p-6 text-left shadow-lg shadow-slate-900/5 transition hover:-translate-y-1 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <div>
                <h3 className="text-lg font-semibold text-slate-900 group-hover:text-emerald-600">{card.title}</h3>
                <p className="mt-2 text-sm text-slate-500">{card.description}</p>
              </div>
              <span className="mt-6 inline-flex items-center text-sm font-semibold text-emerald-600 group-hover:gap-2">
                Manage
                <svg
                  className="ml-1 h-4 w-4 transition group-hover:translate-x-1"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </button>
          ))}
        </section>

        <section className="rounded-3xl border border-dashed border-emerald-200 bg-white/80 p-8 shadow-inner shadow-emerald-50/40">
          <h2 className="text-xl font-semibold text-slate-900">Upload compliance documents</h2>
          <p className="mt-2 text-sm text-slate-500">
            Provide any outstanding KYC documents to complete your onboarding and unlock full access.
          </p>
          <div className="mt-6">
            <DocumentUpload />
          </div>
        </section>
      </div>

      {activeModal === "profile" && (
        <ProfileModal isOpen onClose={() => setActiveModal(null)} user={user} onUpdated={refreshUser} />
      )}
      {activeModal === "password" && <ChangePasswordModal isOpen onClose={() => setActiveModal(null)} />}
    </div>
  );
};

export default Settings;

